import React, { createContext, ReactNode, useContext, useState } from "react";
import { Modal, Box, Typography, Tabs, Tab, IconButton, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

interface IAlert {
  id: number;
  message: string;
  details?: string[]; // Agora detalhes é um array de strings
}

interface IAlertContextData {
  alerts: IAlert[];
  addAlert: (message: string, details?: string[]) => void;
  removeAlert: (id: number) => void;
}

const AlertContext = createContext({} as IAlertContextData);

interface IAlertProviderProps {
  children: ReactNode;
}

function AlertProvider({ children }: IAlertProviderProps) {
  const [alerts, setAlerts] = useState<IAlert[]>([]);
  const [open, setOpen] = useState(false);
  const [activeAlert, setActiveAlert] = useState<IAlert | null>(null);
  const [tabIndex, setTabIndex] = useState(0);

  const addAlert = (message: string, details?: string[]) => {
    const id = new Date().getTime();
    const alert = { id, message, details };
    setAlerts((prev) => [...prev, alert]);
    setActiveAlert(alert);
    setOpen(true); // Abre o modal quando o alerta é adicionado
  };

  const removeAlert = (id: number) => {
    setAlerts((prev) => prev.filter((alert) => alert.id !== id));
    setOpen(false); // Fecha o modal
    setTabIndex(0); // Reseta a aba quando o modal fecha
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  return (
    <AlertContext.Provider value={{ alerts, addAlert, removeAlert }}>
      {children}

      {/* Modal para exibir os alertas */}
      {activeAlert && (
        <Modal open={open} onClose={() => removeAlert(activeAlert.id)}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 3, // Reduzi o padding geral do modal
              borderRadius: 2,
              width: 500,
              maxWidth: "90vw",
              maxHeight: "80vh",
              overflowY: "auto",
            }}
          >
            {/* Header do modal com menos padding e ícone de fechar */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", mb: 1, padding: 0 }}> {/* Removi padding adicional */}
              <IconButton onClick={() => removeAlert(activeAlert.id)} size="large" sx={{ color: "grey.600" }}>
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Tabs para alternar entre alerta e detalhes */}
            <Tabs
              value={tabIndex}
              onChange={handleTabChange}
              centered
              textColor="primary"
              indicatorColor="primary"
              sx={{ mb: 2 }}
            >
              <Tab label="Alerta" />
              {activeAlert.details && <Tab label="Detalhes" />}
            </Tabs>

            {/* Conteúdo da aba Alerta */}
            {tabIndex === 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body1" sx={{ fontSize: "1rem", color: "text.secondary", lineHeight: 1.6 }}>
                  {activeAlert.message}
                </Typography>
              </Box>
            )}

            {/* Conteúdo da aba Detalhes como tabela */}
            {tabIndex === 1 && activeAlert.details && (
              <Box sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Detalhe</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activeAlert.details.map((detail, index) => (
                      <TableRow key={index}>
                        <TableCell>{detail}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Box>
        </Modal>
      )}
    </AlertContext.Provider>
  );
}

function useAlert() {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error("useAlert must be used within an AlertProvider");
  }
  return context;
}

export { AlertProvider, useAlert };
